import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest} from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { PersonalizationKeysEnum } from '@docma-mm-core/personalization/personalization-keys.enum';
import { IMmConfig } from '@docma-mm-core/project/project.interface';
import { ConfigProvider } from '@mydocma/common';
import { CapacitorAppSettingsService } from '../../capacitor-app-settings/Services/capacitor-app-settings.service';
import { FaultUploadType } from '../../capacitor-app-settings/enums/fault-upload-type.enum';
import { PlatformModeStateService } from './platform-mode-state.service';
import { App } from '@capacitor/app';
import { AttachmentDownloadOption } from '../../capacitor-app-settings/enums/attachment-download-option.enum';

@Injectable({
  providedIn: 'root'
})
export class PlatformModeService {

  constructor(private configService: ConfigProvider<IMmConfig>, private capacitorAppSettings: CapacitorAppSettingsService, private platformModeStateService: PlatformModeStateService) {
    this.initializePlatformMode();
    this.initializeNetworkAndUploadWatcher();
  }

  private async initializePlatformMode(): Promise<void> {
    this.platformModeStateService.isNative$.next(Capacitor.isNativePlatform());
    this.platformModeStateService.isIos$.next(Capacitor.getPlatform() === 'ios');
    this.platformModeStateService.isAndroid$.next(Capacitor.getPlatform() === 'android');
    this.platformModeStateService.isLocalModeEnabled$.next(this.configService.getConfigData().localModeConfig?.enabled ?? false);
    const isLocalModeActive = await Preferences.get({key: PersonalizationKeysEnum.IsLocalModeActive});
    this.platformModeStateService.isLocalMode$.next(isLocalModeActive && isLocalModeActive.value === 'true');
    
    const clientUrl = await Preferences.get({key: PersonalizationKeysEnum.CapacitorClientUrl});
    if(clientUrl && clientUrl.value) {
      const urlFolderName = new URL(clientUrl.value).hostname;
      this.platformModeStateService.clientUrl$.next(urlFolderName);
    }

    if(this.platformModeStateService.isNative) {
      const appInfo = await App.getInfo();
      const currentVersion = appInfo.version;
      this.platformModeStateService.currentVersion$.next(currentVersion);
      const useNewAttachmentDownload = !!currentVersion && this.compareVersions(currentVersion, '1.0.1') >= 0;
      this.platformModeStateService.useNewAttachmentDownload$.next(useNewAttachmentDownload);
    }
  }

  private initializeNetworkAndUploadWatcher(): void {
    const networkStatus$ = new BehaviorSubject<ConnectionStatus>({ connected: true, connectionType: 'unknown' });

    Network.getStatus().then(status => {
      this.platformModeStateService.isOnline$.next(status.connected);
      networkStatus$.next(status);
    });

    Network.addListener('networkStatusChange', status => {
      this.platformModeStateService.isOnline$.next(status.connected);
      networkStatus$.next(status);
    });

    const connectionType$ = networkStatus$.pipe(
      map(status => status.connectionType),
      distinctUntilChanged(),
    );

    const faultUploadType$ = this.capacitorAppSettings.faultUploadType$.pipe(distinctUntilChanged());
    const isLocalMode$ = this.platformModeStateService.isLocalMode$.pipe(distinctUntilChanged());

    combineLatest([connectionType$, faultUploadType$, isLocalMode$])
      .subscribe(([connectionType, faultUploadType]) => {
        this.updateFaultUploadPermission(connectionType, faultUploadType);
      });

    combineLatest([networkStatus$, this.capacitorAppSettings.attachmentAutomaticDownload$])
      .pipe(
        map(([status, attachmentAutomaticDownload]) => ({ connectionType: status.connectionType, attachmentAutomaticDownload })),
        distinctUntilChanged((prev, curr) => prev.connectionType === curr.connectionType
          && prev.attachmentAutomaticDownload === curr.attachmentAutomaticDownload),
      )
      .subscribe(({connectionType, attachmentAutomaticDownload}) => {
        this.updateAttachmentDownloadPermission(connectionType, attachmentAutomaticDownload);
      });
  }

  async setLocalMode(value: boolean): Promise<void> {
    if(value != this.platformModeStateService.isLocalMode) {
      await Preferences.set({key: PersonalizationKeysEnum.IsLocalModeActive, value: value.toString()});
    }
    this.platformModeStateService.isLocalMode$.next(value);
  }

  private updateFaultUploadPermission(connectionType: string, faultUploadType: FaultUploadType): void {
    let canUploadFaults: boolean;

    if (!this.platformModeStateService.isLocalModeEnabled || !this.platformModeStateService.isNative || !this.platformModeStateService.isLocalMode) {
      canUploadFaults = true;
    } else if(faultUploadType === FaultUploadType.WifiAndCellular && connectionType !== 'none') {
      canUploadFaults = true;
    } else {
      canUploadFaults = connectionType === faultUploadType;
    }

    if (this.platformModeStateService.canUploadFaults !== canUploadFaults) {
      this.platformModeStateService.canUploadFaults$.next(canUploadFaults);
    }
  }

  private updateAttachmentDownloadPermission(connectionType: string, attachmentAutomaticDownload: string): void {
    if(attachmentAutomaticDownload === AttachmentDownloadOption.Never){
      this.platformModeStateService.canDownloadAttachments$.next(false);
      return;
    }
    else if(attachmentAutomaticDownload === AttachmentDownloadOption.WifiAndCellular) {
      this.platformModeStateService.canDownloadAttachments$.next(true);
      return;
    }
    const canDownloadAttachments = connectionType === attachmentAutomaticDownload;
    this.platformModeStateService.canDownloadAttachments$.next(canDownloadAttachments);
  }

  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    const length = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < length; i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }

    return 0;
  }
}